/**
 * Service levels (#1723/#1725) — the customer-facing packaging over routing
 * configs.
 *
 * One endpoint answers two shapes depending on the caller's role: ops-api
 * proxies the gateway row to an admin and rebuilds an allowlist projection for
 * everybody else. That is why the reads come in pairs rather than one generic
 * call — the admin page asks for `ServiceLevelRecord`, the customer surface
 * asks for `ServiceLevelSummary`, and neither can accidentally be handed the
 * other's type. The record-typed reads are only ever mounted behind the
 * admin-only route guard.
 */
import type {
  CreateServiceLevelRequest,
  ListServiceLevelsResponse,
  OrgServiceLevelState,
  ServiceLevelMutationResponse,
  ServiceLevelSummary,
  SetOrgServiceLevelRequest,
  UpdateServiceLevelRequest,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/service-levels';

/** The catalogue as a customer manager receives it: names, no route identity. */
export interface ServiceLevelCatalogueResponse {
  readonly levels: readonly ServiceLevelSummary[];
}

/**
 * Rebuild the projection field by field instead of forwarding the parsed body.
 *
 * ops-api already strips route identity for a customer manager, but an ADMIN
 * session gets the wider shape off the same endpoint, and both flow through
 * these wrappers into one React Query cache. Narrowing by type alone is a
 * compile-time claim only: at runtime the extra keys would still be there for
 * any `JSON.stringify`, devtools dump, or future generic renderer to surface.
 * Picking the keys makes the type's promise true of the value.
 */
function toSummary(level: ServiceLevelSummary): ServiceLevelSummary {
  return {
    id: level.id,
    name: level.name,
    description: level.description,
    status: level.status,
  };
}

function toOrgView(view: OrgServiceLevelView): OrgServiceLevelView {
  return {
    org_id: view.org_id,
    state: view.state,
    level: view.level === null ? null : toSummary(view.level),
  };
}

/**
 * One org's level state as the customer surface receives it.
 *
 * The admin response is a structural superset (it also carries
 * `routing_config_id`, and its `level` is the full record), so this narrower
 * type is honest for both roles while making it impossible for a routing
 * identity to reach the customer-manager UI through this module.
 */
export interface OrgServiceLevelView {
  readonly org_id: string;
  readonly state: OrgServiceLevelState;
  readonly level: ServiceLevelSummary | null;
}

/** Admin read: full rows, including the anchored routing config. */
export async function listServiceLevels(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListServiceLevelsResponse> {
  return opsApiRequest<ListServiceLevelsResponse>(BASE, { signal }, fetchImpl);
}

/** Customer-surface read: the same endpoint, projected. */
export async function listServiceLevelCatalogue(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ServiceLevelCatalogueResponse> {
  const response = await opsApiRequest<ServiceLevelCatalogueResponse>(BASE, { signal }, fetchImpl);
  return { levels: response.levels.map(toSummary) };
}

export async function createServiceLevel(
  body: CreateServiceLevelRequest,
  fetchImpl: typeof fetch,
): Promise<ServiceLevelMutationResponse> {
  return opsApiRequest<ServiceLevelMutationResponse>(
    BASE,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function updateServiceLevel(
  levelId: string,
  body: UpdateServiceLevelRequest,
  fetchImpl: typeof fetch,
): Promise<ServiceLevelMutationResponse> {
  return opsApiRequest<ServiceLevelMutationResponse>(
    `${BASE}/${encodeURIComponent(levelId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function getOrgServiceLevel(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OrgServiceLevelView> {
  const view = await opsApiRequest<OrgServiceLevelView>(
    `${BASE}/orgs/${encodeURIComponent(orgId)}`,
    { signal },
    fetchImpl,
  );
  return toOrgView(view);
}

export async function setOrgServiceLevel(
  orgId: string,
  body: SetOrgServiceLevelRequest,
  fetchImpl: typeof fetch,
): Promise<OrgServiceLevelView> {
  const view = await opsApiRequest<OrgServiceLevelView>(
    `${BASE}/orgs/${encodeURIComponent(orgId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
  // This value is seeded straight into the query cache, so the admin-shaped
  // response (routing config id, cache generation, mutation receipt) must be
  // narrowed HERE or it becomes cached state the CM card reads from.
  return toOrgView(view);
}
